import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, RefreshCw, Filter, Undo2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { inr } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["all", "successful", "failed", "pending", "processing", "refunded"];

function statusChip(s) {
    if (s === "successful") return "ddi-chip ddi-chip-success";
    if (s === "failed") return "ddi-chip ddi-chip-error";
    if (s === "pending" || s === "processing") return "ddi-chip ddi-chip-warning";
    return "ddi-chip ddi-chip-neutral";
}

export default function AdminPayments() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search] = useSearchParams();
    const [status, setStatus] = useState(search.get("status") || "all");
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(false);
    const [refundTarget, setRefundTarget] = useState(null); // {order_id, doctor, max_refundable}
    const [refundAmount, setRefundAmount] = useState("");
    const [refundNote, setRefundNote] = useState("");
    const [refunding, setRefunding] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const q = new URLSearchParams({ page: String(page), page_size: "25", days: String(days) });
            if (status !== "all") q.set("status", status);
            const r = await api.get(`/admin/payments?${q.toString()}`);
            setRows(r.data.items || []);
            setTotal(r.data.total || 0);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [status, days, page]);

    async function submitRefund() {
        if (!refundTarget) return;
        const amt = parseInt(refundAmount, 10);
        if (!Number.isFinite(amt) || amt < 1) { toast.error("Amount must be a positive integer (INR)"); return; }
        if (amt > refundTarget.max_refundable) { toast.error(`Max refundable ₹${refundTarget.max_refundable}`); return; }
        setRefunding(true);
        try {
            const r = await api.post(`/admin/payments/${refundTarget.order_id}/refund`, {
                amount_inr: amt, note: refundNote || `Refund by admin`,
            });
            const refund = r.data.refund || {};
            toast.success(`Refund ${refund.status} · ₹${refund.amount_inr} · ${refund.cf_refund_id || refund.refund_id}`);
            setRefundTarget(null); setRefundAmount(""); setRefundNote("");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Refund failed"));
        } finally {
            setRefunding(false);
        }
    }

    return (
        <AdminShell>
            <div className="mb-6 flex items-center justify-between" data-testid="admin-payments-header">
                <div>
                    <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Payments</h1>
                    <p className="text-[#64748B] mt-1 text-[14px]">All payments across doctors. Read-only — webhook is the source of truth.</p>
                </div>
                <button onClick={load} disabled={loading} className="ddi-btn-outline" data-testid="admin-payments-refresh">
                    <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <Card className="border-[#E5E9F2] mb-5 ddi-shadow-sm">
                <CardContent className="py-4 flex items-center gap-3 flex-wrap">
                    <Filter size={14} className="text-[#64748B]" />
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#64748B]">Status</span>
                        <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
                            <SelectTrigger className="w-40" data-testid="admin-payments-filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[12px] text-[#64748B]">Range</span>
                        <Select value={String(days)} onValueChange={(v) => { setPage(1); setDays(parseInt(v, 10)); }}>
                            <SelectTrigger className="w-32" data-testid="admin-payments-filter-days"><SelectValue /></SelectTrigger>
                            <SelectContent>{[7, 30, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto text-[12px] text-[#64748B]">
                        {total} payment{total === 1 ? "" : "s"}
                    </div>
                </CardContent>
            </Card>

            <Card className="border-[#E5E9F2] ddi-shadow-sm overflow-hidden">
                {rows.length === 0 ? (
                    <div className="py-16 text-center">
                        <CreditCard size={40} className="mx-auto text-[#94A3B8] mb-3" />
                        <div className="text-[13px] text-[#64748B]">No payments in this filter.</div>
                    </div>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="bg-[#F5F7FB] text-[#334155]">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Doctor</th>
                                <th className="text-left px-4 py-3 font-semibold">Amount</th>
                                <th className="text-left px-4 py-3 font-semibold">Installment</th>
                                <th className="text-left px-4 py-3 font-semibold">Status</th>
                                <th className="text-left px-4 py-3 font-semibold">Gateway</th>
                                <th className="text-left px-4 py-3 font-semibold">Refunds</th>
                                <th className="text-left px-4 py-3 font-semibold">Verified</th>
                                <th className="text-left px-4 py-3 font-semibold">Date</th>
                                <th className="text-left px-4 py-3 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((p) => {
                                const refunded = (p.refunds && p.refunds.total_refunded_inr) || 0;
                                const remaining = Math.max(0, (p.amount_inr || 0) - refunded);
                                const canRefund = p.status === "successful" && remaining > 0;
                                return (
                                <tr key={p.id} className="border-t border-[#EEF1F6]" data-testid={`payment-row-${p.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="text-[#0F172A] font-medium">{p.doctor?.full_name || <span className="text-[#94A3B8]">—</span>}</div>
                                        <div className="text-[11px] text-[#94A3B8]">{p.doctor?.package_code || ""}</div>
                                    </td>
                                    <td className="px-4 py-3 font-semibold text-[#0F172A]">{inr(p.amount_inr)}</td>
                                    <td className="px-4 py-3 capitalize">{p.installment || "—"}</td>
                                    <td className="px-4 py-3"><span className={statusChip(p.status)}>{p.status}</span></td>
                                    <td className="px-4 py-3 text-[#64748B]">{p.gateway || "—"}</td>
                                    <td className="px-4 py-3 text-[11px]" data-testid={`payment-refunds-${p.id}`}>
                                        {refunded > 0
                                            ? <span className="text-[#B45309]" title={`${p.refunds.count} refund(s)`}>Refunded {inr(refunded)} · Rem {inr(remaining)}</span>
                                            : <span className="text-[#94A3B8]">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-[#64748B]">{p.webhook_verified ? "✓" : "—"}</td>
                                    <td className="px-4 py-3 text-[11px] text-[#64748B]">{p.created_at ? new Date(p.created_at).toLocaleString() : "-"}</td>
                                    <td className="px-4 py-3">
                                        {canRefund ? (
                                            <button
                                                onClick={() => { setRefundTarget({ order_id: p.order_id, doctor: p.doctor?.full_name, max_refundable: remaining, gateway: p.gateway }); setRefundAmount(String(remaining)); setRefundNote(""); }}
                                                className="text-[11px] font-semibold text-[#B45309] hover:underline inline-flex items-center gap-1"
                                                data-testid={`refund-btn-${p.id}`}
                                            >
                                                <Undo2 size={12} /> Refund
                                            </button>
                                        ) : <span className="text-[#CBD5E1] text-[11px]">—</span>}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Card>

            {total > 25 && (
                <div className="mt-4 flex items-center justify-center gap-2">
                    <Button variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>Previous</Button>
                    <span className="text-[12px] text-[#64748B]">Page {page} of {Math.ceil(total / 25)}</span>
                    <Button variant="outline" onClick={() => setPage(page + 1)} disabled={page * 25 >= total}>Next</Button>
                </div>
            )}

            <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
                <DialogContent data-testid="refund-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Refund payment</DialogTitle>
                    </DialogHeader>
                    {refundTarget && (
                        <div className="space-y-3 text-[13px]">
                            <div className="text-[#64748B]">Doctor: <strong className="text-[#0F172A]">{refundTarget.doctor}</strong></div>
                            <div className="text-[#64748B]">Order: <code className="text-[11px]">{refundTarget.order_id}</code></div>
                            <div className="text-[#64748B]">Gateway: <strong>{refundTarget.gateway}</strong></div>
                            <div className="text-[#64748B]">Max refundable: <strong className="text-[#0F172A]">{inr(refundTarget.max_refundable)}</strong></div>
                            <div>
                                <Label className="text-xs">Amount (₹, whole rupees)</Label>
                                <Input type="number" min="1" max={refundTarget.max_refundable} value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount-input" />
                            </div>
                            <div>
                                <Label className="text-xs">Note (optional)</Label>
                                <Input value={refundNote} onChange={(e) => setRefundNote(e.target.value)} placeholder="Reason shown in audit log" data-testid="refund-note-input" />
                            </div>
                            <div className="text-[11px] text-[#94A3B8]">Cashfree refunds start in PENDING state and settle in 5–7 business days. Mock refunds are marked success instantly.</div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRefundTarget(null)} disabled={refunding} data-testid="refund-cancel">Cancel</Button>
                        <Button onClick={submitRefund} disabled={refunding} data-testid="refund-submit">
                            {refunding ? "Refunding…" : "Confirm refund"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminShell>
    );
}
